import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  RefreshCw,
  Download,
  TrendingUp,
  Users,
  AlertTriangle,
  Hash,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Globe,
  CheckCircle,
  FileDown,
  Loader2,
  SearchX,
  ScanBarcode,
} from 'lucide-react';
import {
  fetchSearchOverview,
  fetchTopQueries,
  fetchNoResultQueries,
  fetchSearchTrends,
  fetchSearchByCountry,
  fetchCountriesForDropdown,
  exportSearchKeywords,
  SearchOverview,
  TopQuery,
  NoResultQuery,
  TrendDataPoint,
  CountryBreakdown,
} from '../services/api.ts';
import './SearchAnalytics.css';

type TabType = 'overview' | 'top-queries' | 'no-results' | 'countries';
type DateRange = '7' | '30' | '90' | 'custom';
type GroupBy = 'day' | 'week' | 'month';

const SearchAnalytics: React.FC = () => {
  // Filter state
  const [selectedCountry, setSelectedCountry] = useState<number | undefined>(undefined);
  const [dateRange, setDateRange] = useState<DateRange>('30');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [searchTypeFilter, setSearchTypeFilter] = useState<string>('');

  // Data state
  const [overview, setOverview] = useState<SearchOverview | null>(null);
  const [topQueries, setTopQueries] = useState<TopQuery[]>([]);
  const [topQueriesTotal, setTopQueriesTotal] = useState(0);
  const [noResults, setNoResults] = useState<NoResultQuery[]>([]);
  const [noResultsTotal, setNoResultsTotal] = useState(0);
  const [trends, setTrends] = useState<TrendDataPoint[]>([]);
  const [countries, setCountries] = useState<CountryBreakdown[]>([]);
  const [countryList, setCountryList] = useState<{ id: number; name: string }[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tqPage, setTqPage] = useState(0);
  const [nrPage, setNrPage] = useState(0);
  const [trendGroupBy, setTrendGroupBy] = useState<GroupBy>('day');
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const PAGE_SIZE = 20;

  // Calculate date range
  const getDateFilters = useCallback(() => {
    if (dateRange === 'custom') {
      return { dateFrom: customDateFrom || undefined, dateTo: customDateTo || undefined };
    }
    const now = new Date();
    const from = new Date(now);
    from.setDate(from.getDate() - parseInt(dateRange));
    return {
      dateFrom: from.toISOString().split('T')[0],
      dateTo: now.toISOString().split('T')[0],
    };
  }, [dateRange, customDateFrom, customDateTo]);

  // Show toast notification
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Load countries dropdown
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await fetchCountriesForDropdown();
        setCountryList(data || []);
      } catch (err) {
        console.error('Failed to load countries:', err);
      }
    };
    loadCountries();
  }, []);

  // Load data
  const loadData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      else setRefreshing(true);
      setError(null);

      const { dateFrom, dateTo } = getDateFilters();

      const [overviewData, trendsData, countriesData] = await Promise.all([
        fetchSearchOverview(selectedCountry, dateFrom, dateTo, searchTypeFilter || undefined),
        fetchSearchTrends({ countryId: selectedCountry, dateFrom, dateTo, groupBy: trendGroupBy }),
        fetchSearchByCountry(dateFrom, dateTo),
      ]);

      setOverview(overviewData);
      setTrends(trendsData.data);
      setCountries(countriesData.countries);

      // Also load the active tab data
      if (activeTab === 'top-queries' || activeTab === 'overview') {
        const tqData = await fetchTopQueries({
          countryId: selectedCountry, dateFrom, dateTo, searchType: searchTypeFilter || undefined, offset: tqPage * PAGE_SIZE, limit: PAGE_SIZE,
        });
        setTopQueries(tqData.queries);
        setTopQueriesTotal(tqData.total);
      }
      if (activeTab === 'no-results' || activeTab === 'overview') {
        const nrData = await fetchNoResultQueries({
          countryId: selectedCountry, dateFrom, dateTo, offset: nrPage * PAGE_SIZE, limit: PAGE_SIZE,
        });
        setNoResults(nrData.queries);
        setNoResultsTotal(nrData.total);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedCountry, getDateFilters, trendGroupBy, activeTab, tqPage, nrPage, searchTypeFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle export
  const handleExport = async (resultsFilter: 'all' | 'no_results' | 'with_results' = 'all') => {
    try {
      setExporting(true);
      const { dateFrom, dateTo } = getDateFilters();
      await exportSearchKeywords({
        countryId: selectedCountry,
        dateFrom,
        dateTo,
        minSearches: 1,
        resultsFilter,
      });
      showToast('Keywords exported successfully', 'success');
      setExportModalOpen(false);
    } catch (err: any) {
      showToast('Export failed: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setExporting(false);
    }
  };

  // Format number
  const formatNum = (n: number) => {
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toString();
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="search-analytics">
        <div className="sa-loading">
          <Loader2 size={32} />
          <p>Loading search analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="search-analytics">
      {/* Toast */}
      {toast && (
        <div className={`sa-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="sa-header">
        <div className="sa-header-left">
          <h2><Search size={24} /> Search Analytics</h2>
        </div>
        <div className="sa-header-right">
          <button
            className="sa-btn sa-btn-export"
            onClick={() => setExportModalOpen(true)}
          >
            <Download size={16} /> Export Keywords
          </button>
          <button
            className={`sa-btn-icon ${refreshing ? 'spinning' : ''}`}
            onClick={() => loadData(false)}
            disabled={refreshing}
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="sa-filters">
        <div className="sa-filter-group">
          <label>Country</label>
          <select
            value={selectedCountry || ''}
            onChange={(e) => {
              setSelectedCountry(e.target.value ? parseInt(e.target.value) : undefined);
              setTqPage(0);
              setNrPage(0);
            }}
          >
            <option value="">All Countries</option>
            {countryList.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="sa-filter-group">
          <label>Period</label>
          <select value={dateRange} onChange={(e) => { setDateRange(e.target.value as DateRange); setTqPage(0); setNrPage(0); }}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        {dateRange === 'custom' && (
          <>
            <div className="sa-filter-group">
              <label>From</label>
              <input type="date" value={customDateFrom} onChange={(e) => setCustomDateFrom(e.target.value)} />
            </div>
            <div className="sa-filter-group">
              <label>To</label>
              <input type="date" value={customDateTo} onChange={(e) => setCustomDateTo(e.target.value)} />
            </div>
          </>
        )}
        <div className="sa-filter-group">
          <label>Type</label>
          <select value={searchTypeFilter} onChange={(e) => { setSearchTypeFilter(e.target.value); setTqPage(0); }}>
            <option value="">All Types</option>
            <option value="search">Text Search</option>
            <option value="barcode">Barcode Scan</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="sa-error">
          <AlertTriangle size={20} />
          <p>{error}</p>
          <button className="sa-btn sa-btn-secondary" onClick={() => loadData()}>Retry</button>
        </div>
      )}

      {/* Stat Cards */}
      {overview && (
        <div className="sa-stats-grid">
          <div className="sa-stat-card">
            <div className="sa-stat-header">
              <span className="sa-stat-label">Total Searches</span>
              <div className="sa-stat-icon blue"><Search size={18} /></div>
            </div>
            <div className="sa-stat-value">{formatNum(overview.total_searches)}</div>
            <div className={`sa-stat-change ${overview.total_searches_change >= 0 ? 'positive' : 'negative'}`}>
              {overview.total_searches_change >= 0 ? '↑' : '↓'} {Math.abs(overview.total_searches_change)}% vs prev period
            </div>
          </div>

          <div className="sa-stat-card">
            <div className="sa-stat-header">
              <span className="sa-stat-label">Unique Users</span>
              <div className="sa-stat-icon green"><Users size={18} /></div>
            </div>
            <div className="sa-stat-value">{formatNum(overview.unique_users)}</div>
            <div className={`sa-stat-change ${overview.unique_users_change >= 0 ? 'positive' : 'negative'}`}>
              {overview.unique_users_change >= 0 ? '↑' : '↓'} {Math.abs(overview.unique_users_change)}% vs prev period
            </div>
          </div>

          <div className="sa-stat-card">
            <div className="sa-stat-header">
              <span className="sa-stat-label">No-Result Rate</span>
              <div className="sa-stat-icon red"><AlertTriangle size={18} /></div>
            </div>
            <div className="sa-stat-value">{overview.no_result_rate}%</div>
            <div className={`sa-stat-change ${overview.no_result_rate_change <= 0 ? 'positive' : 'negative'}`}>
              {overview.no_result_rate_change <= 0 ? '↓' : '↑'} {Math.abs(overview.no_result_rate_change)}pp vs prev period
            </div>
          </div>

          <div className="sa-stat-card">
            <div className="sa-stat-header">
              <span className="sa-stat-label">Unique Queries</span>
              <div className="sa-stat-icon purple"><Hash size={18} /></div>
            </div>
            <div className="sa-stat-value">{formatNum(overview.unique_queries)}</div>
            <div className="sa-stat-change neutral">
              {overview.zero_result_count} with zero results
            </div>
          </div>

          <div className="sa-stat-card">
            <div className="sa-stat-header">
              <span className="sa-stat-label">Barcode Scans</span>
              <div className="sa-stat-icon orange"><ScanBarcode size={18} /></div>
            </div>
            <div className="sa-stat-value">{formatNum(overview.barcode_searches)}</div>
            <div className="sa-stat-change neutral">
              {formatNum(overview.text_searches)} text searches
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="sa-tabs">
        <button className={`sa-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
          <BarChart3 size={16} /> Overview
        </button>
        <button className={`sa-tab ${activeTab === 'top-queries' ? 'active' : ''}`} onClick={() => { setActiveTab('top-queries'); setTqPage(0); }}>
          <TrendingUp size={16} /> Top Queries <span className="badge">{topQueriesTotal}</span>
        </button>
        <button className={`sa-tab ${activeTab === 'no-results' ? 'active' : ''}`} onClick={() => { setActiveTab('no-results'); setNrPage(0); }}>
          <SearchX size={16} /> No Results <span className="badge">{noResultsTotal}</span>
        </button>
        <button className={`sa-tab ${activeTab === 'countries' ? 'active' : ''}`} onClick={() => setActiveTab('countries')}>
          <Globe size={16} /> Countries <span className="badge">{countries.length}</span>
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Trend Chart (simplified table-based since no chart library) */}
          <div className="sa-section">
            <div className="sa-section-header">
              <h3><TrendingUp size={18} /> Search Trends</h3>
              <div className="sa-section-actions">
                <div className="sa-chart-toggle">
                  {(['day', 'week', 'month'] as GroupBy[]).map((g) => (
                    <button key={g} className={trendGroupBy === g ? 'active' : ''} onClick={() => setTrendGroupBy(g)}>
                      {g.charAt(0).toUpperCase() + g.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="sa-section-body">
              {trends.length > 0 ? (
                <table className="sa-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Searches</th>
                      <th>Unique Users</th>
                      <th>Zero Results</th>
                      <th>Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...trends].reverse().map((point, i) => {
                      const maxCount = Math.max(...trends.map(t => t.search_count), 1);
                      const barWidth = (point.search_count / maxCount) * 100;
                      return (
                        <tr key={i}>
                          <td>{point.date ? formatDate(point.date) : 'N/A'}</td>
                          <td><strong>{point.search_count}</strong></td>
                          <td>{point.unique_users}</td>
                          <td>{point.zero_results}</td>
                          <td style={{ width: '200px' }}>
                            <div style={{ background: '#e5e7eb', borderRadius: '4px', height: '8px', width: '100%' }}>
                              <div style={{ background: '#6366f1', borderRadius: '4px', height: '8px', width: `${barWidth}%`, transition: 'width 0.3s' }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="sa-empty">
                  <BarChart3 size={32} />
                  <p>No trend data available for this period</p>
                </div>
              )}
            </div>
          </div>

          {/* Trending Queries Quick View */}
          {overview && overview.trending.length > 0 && (
            <div className="sa-section">
              <div className="sa-section-header">
                <h3><TrendingUp size={18} /> Trending Queries (Top 10)</h3>
                <button className="sa-btn sa-btn-secondary" onClick={() => { setActiveTab('top-queries'); setTqPage(0); }}>
                  View All →
                </button>
              </div>
              <div className="sa-section-body">
                <table className="sa-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Query</th>
                      <th>Searches</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.trending.map((item, i) => (
                      <tr key={i}>
                        <td className="rank">{i + 1}</td>
                        <td className="query-text">{item.query}</td>
                        <td><strong>{item.search_count}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Top Queries Tab */}
      {activeTab === 'top-queries' && (
        <div className="sa-section">
          <div className="sa-section-header">
            <h3><TrendingUp size={18} /> Top Search Queries</h3>
            <div className="sa-section-actions">
              <button className="sa-btn sa-btn-export" onClick={() => handleExport('with_results')}>
                <Download size={14} /> Export
              </button>
            </div>
          </div>
          <div className="sa-section-body" style={{ padding: 0 }}>
            {topQueries.length > 0 ? (
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Query</th>
                    <th>Type</th>
                    <th>Searches</th>
                    <th>Unique Users</th>
                    <th>Avg Results</th>
                  </tr>
                </thead>
                <tbody>
                  {topQueries.map((q) => (
                    <tr key={q.rank}>
                      <td className="rank">{q.rank}</td>
                      <td className="query-text">{q.query}</td>
                      <td>
                        <span className={`sa-type-badge ${q.search_type === 'barcode' ? 'barcode' : 'text'}`}>
                          {q.search_type === 'barcode' ? '📷 Barcode' : '🔍 Search'}
                        </span>
                      </td>
                      <td><strong>{q.search_count}</strong></td>
                      <td>{q.unique_users}</td>
                      <td>{q.avg_results}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="sa-empty">
                <Search size={32} />
                <p>No search queries found for this period</p>
              </div>
            )}
          </div>
          {topQueriesTotal > PAGE_SIZE && (
            <div className="sa-pagination">
              <span className="sa-pagination-info">
                Showing {tqPage * PAGE_SIZE + 1}–{Math.min((tqPage + 1) * PAGE_SIZE, topQueriesTotal)} of {topQueriesTotal}
              </span>
              <div className="sa-pagination-controls">
                <button disabled={tqPage === 0} onClick={() => setTqPage(tqPage - 1)}>
                  <ChevronLeft size={14} /> Prev
                </button>
                <button disabled={(tqPage + 1) * PAGE_SIZE >= topQueriesTotal} onClick={() => setTqPage(tqPage + 1)}>
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* No Results Tab */}
      {activeTab === 'no-results' && (
        <div className="sa-section">
          <div className="sa-section-header">
            <h3><SearchX size={18} /> Zero-Result Queries (Inventory Gaps)</h3>
            <div className="sa-section-actions">
              <button className="sa-btn sa-btn-export" onClick={() => handleExport('no_results')}>
                <Download size={14} /> Export
              </button>
            </div>
          </div>
          <div className="sa-section-body" style={{ padding: 0 }}>
            {noResults.length > 0 ? (
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Query</th>
                    <th>Searches</th>
                    <th>Last Searched</th>
                    <th>Countries</th>
                  </tr>
                </thead>
                <tbody>
                  {noResults.map((q, i) => {
                    const badgeClass = q.search_count >= 50 ? 'high' : q.search_count >= 10 ? 'medium' : 'low';
                    return (
                      <tr key={i}>
                        <td className="query-text">{q.query}</td>
                        <td>
                          <span className={`count-badge ${badgeClass}`}>{q.search_count}</span>
                        </td>
                        <td>{q.last_searched ? formatDate(q.last_searched) : 'N/A'}</td>
                        <td>{q.country_count}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="sa-empty">
                <CheckCircle size={32} />
                <p>No zero-result queries found — great coverage!</p>
              </div>
            )}
          </div>
          {noResultsTotal > PAGE_SIZE && (
            <div className="sa-pagination">
              <span className="sa-pagination-info">
                Showing {nrPage * PAGE_SIZE + 1}–{Math.min((nrPage + 1) * PAGE_SIZE, noResultsTotal)} of {noResultsTotal}
              </span>
              <div className="sa-pagination-controls">
                <button disabled={nrPage === 0} onClick={() => setNrPage(nrPage - 1)}>
                  <ChevronLeft size={14} /> Prev
                </button>
                <button disabled={(nrPage + 1) * PAGE_SIZE >= noResultsTotal} onClick={() => setNrPage(nrPage + 1)}>
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Countries Tab */}
      {activeTab === 'countries' && (
        <div className="sa-section">
          <div className="sa-section-header">
            <h3><Globe size={18} /> Search by Country</h3>
          </div>
          <div className="sa-section-body">
            {countries.length > 0 ? (
              <div className="sa-country-grid">
                {countries.map((c) => (
                  <div
                    key={c.country_id}
                    className="sa-country-card"
                    onClick={() => {
                      setSelectedCountry(c.country_id);
                      setActiveTab('overview');
                      setTqPage(0);
                      setNrPage(0);
                    }}
                  >
                    <div className="sa-country-card-header">
                      <span className="sa-country-name">{c.country_name}</span>
                      <span className="sa-country-pct">{c.percentage}%</span>
                    </div>
                    <div className="sa-country-stats">
                      <div className="sa-country-stat">
                        <span className="sa-country-stat-label">Searches</span>
                        <span className="sa-country-stat-value">{formatNum(c.total_searches)}</span>
                      </div>
                      <div className="sa-country-stat">
                        <span className="sa-country-stat-label">Users</span>
                        <span className="sa-country-stat-value">{formatNum(c.unique_users)}</span>
                      </div>
                    </div>
                    {c.top_query && (
                      <div className="sa-country-top-query">
                        Top query: <span>{c.top_query}</span>
                      </div>
                    )}
                    <div className="sa-country-bar">
                      <div className="sa-country-bar-fill" style={{ width: `${c.percentage}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="sa-empty">
                <Globe size={32} />
                <p>No country data available for this period</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Export Modal */}
      {exportModalOpen && (
        <div className="sa-modal-overlay" onClick={() => !exporting && setExportModalOpen(false)}>
          <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
            <h3><FileDown size={20} /> Export Search Keywords</h3>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 16px 0' }}>
              Export search keywords as CSV for import into pcf-product application.
            </p>
            <div className="sa-modal-info">
              <div className="sa-modal-info-row">
                <span className="sa-modal-info-label">Country</span>
                <span className="sa-modal-info-value">
                  {selectedCountry ? countryList.find(c => c.id === selectedCountry)?.name || 'Selected' : 'All Countries'}
                </span>
              </div>
              <div className="sa-modal-info-row">
                <span className="sa-modal-info-label">Period</span>
                <span className="sa-modal-info-value">
                  {dateRange === 'custom' ? `${customDateFrom} to ${customDateTo}` : `Last ${dateRange} days`}
                </span>
              </div>
              <div className="sa-modal-info-row">
                <span className="sa-modal-info-label">Format</span>
                <span className="sa-modal-info-value">CSV (search_keyword, search_count, country_id, country_name)</span>
              </div>
            </div>
            <div className="sa-modal-actions">
              <button className="sa-btn sa-btn-secondary" onClick={() => setExportModalOpen(false)} disabled={exporting}>
                Cancel
              </button>
              <button className="sa-btn sa-btn-secondary" onClick={() => handleExport('no_results')} disabled={exporting}>
                {exporting ? <Loader2 size={14} /> : <Download size={14} />} No-Results Only
              </button>
              <button className="sa-btn sa-btn-export" onClick={() => handleExport('all')} disabled={exporting}>
                {exporting ? <Loader2 size={14} /> : <Download size={14} />} Download All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchAnalytics;
